import { memo, useCallback, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { useAv, useAvPubsubMessaging } from "../../utils/av";

import { AnimatedBannerWrapper } from "./AnimatedBannerWrapper";
import { BaseFooter } from "./BaseFooter";
import { JoinInPersonClassButton } from "./JoinInPersonClassButton";
import ToolsMenu from "./tools-menu/ToolsMenu";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { ScreenshareOptions } from "components/av/ScreenshareOptions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { EditModeButton } from "components/footer/EditModeButton";
import EndClassMenu from "components/footer/EndClassMenu";
import { PillBanner } from "components/footer/PillBanner";
import { PillBannerButton } from "components/footer/PillBannerButton";
import { PublishBanner } from "components/footer/PublishBanner";
import { ReactionFeed } from "components/footer/ReactionFeed";
import { TimerBanner } from "components/footer/TimerBanner";
import { IconButtons } from "components/footer/icon-buttons/IconButtons";
import { PresentationOptions } from "components/materials/presentation/PresentationOptions";
import { ResumePresentationBanner } from "components/materials/presentation/ResumePresentationBanner";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { VideoIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentSection,
  selectCurrentSectionIsArchived,
  selectCurrentSectionUserId,
  selectIsClassSessionInPerson,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { selectScreensharingClassUserId } from "store/selectors/virtual-classes";
import { viewStateActions } from "store/slices/view";
import { useObjectSelector } from "store/store-hooks";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { useResumePresentation } from "utils/hooks/use-resume-presentation";
import { FeatureStatus, usePresentation } from "utils/presentation";
import { isLinkedSection, isTemplateSection } from "utils/publishing";
import { isCCMEnabled } from "utils/waffle-flags";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));
const LazyBreakoutsBanner = withActiveAv(
  () => import("components/footer/breakouts-banner/BreakoutsBanner"),
  undefined,
  true,
);

export const InstructorMainFooter = memo(() => {
  const isMobile = !useIsMdOrLarger();
  const isLargeScreen = useIsLgOrLarger();
  const isAssessing = useIsAssessing();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { canObserveVirtualClass } = useCapabilities();
  const isClassSessionInPerson = useAppSelector(selectIsClassSessionInPerson);
  const currentSectionUserId = useAppSelector(selectCurrentSectionUserId);
  const currentSection = useObjectSelector(selectCurrentSection, ["id", "kind"]);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isLinked = isLinkedSection(currentSection);
  const isTemplate = isTemplateSection(currentSection);
  const ccmEnabled = isCCMEnabled();
  const {
    startObservingVirtualClass,
    stopObservingVirtualClass,
    showAvCheckToJoinVirtualClass,
    stopVirtualClass,
  } = useVirtualClassActions();
  const {
    isVirtualClassActive,
    isInVirtualClass,
    isInVirtualClassOrObserving,
    isAvActiveInAnotherTab,
    isAvSupportedInBrowser,
  } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;

  const controlsRef = useRef<HTMLDivElement>(null);
  const { featureStatus, isPresentingUser } = usePresentation();
  const dispatch = useAppDispatch();
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass) && isVirtualClassActive;
  const shouldShowObservingControls = canObserveVirtualClass && !currentSectionUserId;
  const { showResumePresentationBanner, onResumePresentation, onDismissPausedPresentation } =
    useResumePresentation();

  const onJoinVirtualClass = useCallback(() => {
    showAvCheckToJoinVirtualClass();
  }, [showAvCheckToJoinVirtualClass]);

  const onMoveVirtualClassToThisTab = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "join",
      force: true,
    });
  }, [sendPubsubVirtualClassStateMessage]);

  const onStartObservingVirtualClass = useCallback(() => {
    startObservingVirtualClass();
  }, [startObservingVirtualClass]);

  const onStopObservingVirtualClass = useCallback(() => {
    stopObservingVirtualClass();
  }, [stopObservingVirtualClass]);

  const joinButton = (
    <PillBannerButton
      eventName="Join Class"
      kind="blue"
      title={t("footer.join_session")}
      onClick={onJoinVirtualClass}
    >
      {t("footer.join_session")}
    </PillBannerButton>
  );

  const observeButton = (
    <Tooltip content={t("av.can_only_observe")}>
      <PillBannerButton
        eventName="Join Class as Observer"
        kind="blue"
        title={t("av.observe_class")}
        onClick={onStartObservingVirtualClass}
      >
        {t("av.observe_class")}
      </PillBannerButton>
    </Tooltip>
  );

  const stopObservingButton = (
    <PillBannerButton
      eventName="Stop Observing Class"
      kind="blue"
      title={t("av.stop_observing_class")}
      onClick={onStopObservingVirtualClass}
    >
      {t("av.stop_observing_class")}
    </PillBannerButton>
  );

  const endButton = (
    <PillBannerButton
      className="border border-red-tint-70 bg-red-tint-99"
      disabled={shouldShowObservingControls}
      eventName="End Class for All"
      title={t("av.stop_virtual_class_short")}
      onClick={stopVirtualClass}
    >
      {t("av.stop_virtual_class_short")}
    </PillBannerButton>
  );

  // Define the final buttons with conditional tooltip wrapping
  const joinOrObserveButton = isObservingClass
    ? stopObservingButton
    : shouldShowObservingControls
      ? observeButton
      : joinButton;

  const endButtonWithTooltip = shouldShowObservingControls ? null : endButton;

  // Prevent the feature modal from closing when clicking on the footer
  useEffect(() => {
    const keepFooterOpenOnMouseDown = () => {
      dispatch(viewStateActions.setPreventFeatureModalClosing(true));
    };

    const controls = controlsRef.current;

    if (isPresentingUser && isFeaturing && controls) {
      controls.addEventListener("mousedown", keepFooterOpenOnMouseDown);
    }

    return () => {
      controls?.removeEventListener("mousedown", keepFooterOpenOnMouseDown);
    };
  }, [isPresentingUser, isFeaturing, dispatch]);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (isVirtualClassActive && isAvActiveInAnotherTab) {
      createMonitoringEvent(MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB, {});
    }
  }, [isVirtualClassActive, isAvActiveInAnotherTab, createMonitoringEvent]);

  const MaybeJoinVirtualClassButton =
    isVirtualClassActive && !isInVirtualClass && isAvSupportedInBrowser ? (
      <PillBanner
        buttonChildren={
          isAvActiveInAnotherTab ? (
            <PillBannerButton
              eventName="Move Virtual Class to This Tab"
              kind="blue"
              title={t("av.move_virtual_class_to_this_tab")}
              onClick={onMoveVirtualClassToThisTab}
            >
              {t("av.move_virtual_class_to_this_tab")}
            </PillBannerButton>
          ) : (
            <>
              {joinOrObserveButton}
              {endButtonWithTooltip}
            </>
          )
        }
        kind="blue"
        textChildren={
          <IconText
            iconStart={<VideoIcon className="text-blue-shade-40" />}
            text={
              isAvActiveInAnotherTab
                ? t("av.virtual_class_in_another_tab")
                : t("av.virtual_class_in_session")
            }
            truncateText
          />
        }
      />
    ) : null;

  const MaybeJoinInPersonClassButton =
    isClassSessionInPerson && !isInInPersonClass ? <JoinInPersonClassButton isInstructor /> : null;
  const canViewClass = isInClassSession || isObservingClass;

  const shouldShouldShowBreakoutsBanner =
    isVirtualClassActive && isInVirtualClassOrObserving && isInBreakout;
  const shouldShowEditModeButton = (isLargeScreen || (!isInClassSession && !isMobile)) && !isLinked;

  return (
    <BaseFooter darkMode={canViewClass}>
      {isInClassSession && <ReactionFeed />}
      <div
        className={twMerge(
          "flex h-full w-full items-center",
          isInClassSession ? "justify-between" : "justify-center",
        )}
      >
        <div className="pr-2">
          <LazyVideoMicControls />
        </div>

        <div className="flex h-full w-full min-w-0 items-center justify-center gap-2">
          <div className="flex h-full min-w-0 items-center justify-end gap-2" ref={controlsRef}>
            {MaybeJoinVirtualClassButton}
            {MaybeJoinInPersonClassButton}

            <AnimatedBannerWrapper isVisible={showResumePresentationBanner}>
              {showResumePresentationBanner && (
                <ResumePresentationBanner
                  onDismiss={onDismissPausedPresentation}
                  onResume={onResumePresentation}
                />
              )}
            </AnimatedBannerWrapper>
            {!showResumePresentationBanner &&
              (screensharingClassUserId ? (
                <ScreenshareOptions />
              ) : canViewClass ? (
                <PresentationOptions isInClassSession={isInClassSession} />
              ) : null)}

            {isInClassSession && <ToolsMenu />}

            {ccmEnabled && isTemplate && <PublishBanner />}

            {shouldShowEditModeButton && (
              <EditModeButton
                disabledTooltipText={
                  isArchived ? t("tooltip.archived_section_edit_mode") : undefined
                }
                isDisabled={isAssessing || isArchived}
              />
            )}

            {shouldShouldShowBreakoutsBanner && <LazyBreakoutsBanner />}
            {isInClassSession && <TimerBanner />}
          </div>

          <IconButtons />
        </div>

        {isInClassSession && <EndClassMenu />}
      </div>
    </BaseFooter>
  );
});

InstructorMainFooter.displayName = "InstructorMainFooter";
